/**
 * The one place query keys are defined.
 *
 * Every key below is byte-identical to the ad-hoc array it replaces at the call
 * site. That is a hard requirement, not a nicety: keys are hashed into the
 * IndexedDB persister's entry ids (`experimental_createQueryPersister`, one
 * entry per query hash), so changing a key's *shape* silently orphans every
 * reader's persisted copy of that query and they lose offline access to it
 * until the next successful online fetch. If you need to change a key shape,
 * bump `config.build.version` so the persister's `buster` clears the old
 * entries deliberately rather than leaving them stranded.
 */
export const queryKeys = {
	issues: () => ['issues'] as const,
	// `pages/issues`' own list page, a genuinely different GraphQL operation
	// (`Issues`, no variables) from `issues()` above (`GetIssuesOptions`,
	// sortKey/limit/reverse/offset). Added 2026-08-19: `pages/issues/model/
	// issues.hooks.ts`'s `useGetIssues` previously used the literal key
	// `['issues']`, silently colliding with `issues()` above -- two
	// different queries sharing one cache slot. Deliberately not reusing
	// `issues()`'s key even though the shape would technically fit; that
	// would just move the collision, not fix it.
	issuesList: () => ['issues', 'list'] as const,
	issue: (id: string | number, params?: IssueQueryParams) =>
		params ? (['issue', id, params] as const) : (['issue', id] as const),
	latestIssue: () => ['issue', 'latest'] as const,
	issueFeed: (id: string | number) => ['issue', id, { feed: true }] as const,

	article: (id: string | number) => ['article', id] as const,
	latestFeatureArticle: () => ['latest', 'feature', 'article'] as const,

	preview: (id: string | number) => ['preview', id] as const,

	sections: () => ['sections'] as const,
	sectionIssues: (id: string | number) => ['sections', id, 'issues'] as const,
	section: (id: string | number) => ['section', id] as const,

	collectionIssue: (id: string | number) =>
		['collections', id, 'issue'] as const,

	/**
	 * An external RSS feed, keyed by its URL (poets' top-stories / daily-news
	 * widgets). The URL is the identity here -- it comes from tenant config,
	 * and two widgets on the same page can point at different feeds.
	 */
	rssFeed: (url: string) => ['rss-feed', url] as const,

	games: () => ['games'] as const,
	products: () => ['products'] as const,
	assets: () => ['assets'] as const,
	fonts: () => ['fonts'] as const,
};

/**
 * `useIssue` keys on the search params that change what the server returns, so
 * two different sort/limit views of the same issue don't collide in the cache.
 */
export interface IssueQueryParams {
	limit?: number;
	sortKey?: string;
	reverse?: boolean;
}
